import {
	useGetUploadUrlMutation,
	useSetDocumentPendingMutation,
	useUploadS3Mutation,
} from "@api/documentManagement";
import { showAlert } from "@components/alert/action";
import { message } from "antd";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

export const useDocumentUpload = (translate) => {
	const [uploadingDocuments, setUploadingDocuments] = useState(new Set());
	const dispatch = useDispatch();
	const [getUploadUrl] = useGetUploadUrlMutation();
	const [setDocumentPending] = useSetDocumentPendingMutation();
	const [uploadS3] = useUploadS3Mutation();

	const handleBeforeUpload = useCallback(
		(file) => {
			const isJPG = file.type === "image/jpeg";
			const isPNG = file.type === "image/png";
			const isBMP = file.type === "image/bmp";
			const isPDF = file.type === "application/pdf";
			if (isJPG || isPNG || isPDF || isBMP) {
				const isLt2M = file.size / 1024 / 1024 < 5;
				if (!isLt2M) {
					message.error(
						translate("FILE_SIZE_ERROR") || "File size must be smaller than 5MB"
					);
					return false;
				}
				return true;
			} else {
				message.error(
					translate("FILE_TYPE_ERROR") || "Only JPG, PNG, PDF, and BMP files are allowed"
				);
				return false;
			}
		},
		[translate]
	);

	const customUpload = useCallback(
		async ({ file, record }) => {
			if (!file || !record) return;

			setUploadingDocuments((prev) => new Set(prev).add(record.documentName));
			try {
				const name = `${file.name.split(".")[0]}_${new Date().getTime()}`;
				const urlRes = await getUploadUrl({
					fileName: name,
					contentType: file.type,
					documentName: record.documentName,
				}).unwrap();
				const uploadUrl =
					urlRes.data?.urlToUpload ||
					urlRes.data?.url ||
					urlRes.urlToUpload ||
					urlRes.url;
				if (uploadUrl) {
					await uploadS3({ url: uploadUrl, file }).unwrap();
					await setDocumentPending({
						documentName: record.documentName,
						note: "",
						status: "Pending",
					}).unwrap();
					dispatch(
						showAlert({
							type: "success",
							msg: "SUCCESSFULL_UPLOAD",
							title: "SUCCESSFULL_TITLE",
						})
					);
				} else {
					message.error(translate("Failed to get upload URL"));
				}
			} catch (error) {
				message.error(translate("Failed to upload document"));
			} finally {
				setUploadingDocuments((prev) => {
					const newSet = new Set(prev);
					newSet.delete(record.documentName);
					return newSet;
				});
			}
		},
		[getUploadUrl, setDocumentPending, translate, uploadS3, dispatch]
	);

	const isDocumentUploading = useCallback(
		(documentName) => uploadingDocuments.has(documentName),
		[uploadingDocuments]
	);

	return {
		handleBeforeUpload,
		customUpload,
		isDocumentUploading,
	};
};
